import { capture } from '@lib/posthog-capture'
import { parseUrl, originPathname } from '@lib/posthog-url-sanitize'

/**
 * Data minimization: only the path and referrer origin+pathname are sent.
 * Query strings and hashes can carry signed params or user input, so both
 * are stripped before anything leaves the browser — same `parseUrl`/
 * `originPathname` pair `lib/web-vitals-lcp.ts` uses for image URLs.
 */
function sanitizePath(raw: string): string {
  const url = parseUrl(raw, typeof location === 'undefined' ? undefined : location.href)
  return url ? url.pathname : raw
}

/** Origin+pathname of the referrer, or null when there wasn't one (direct hit, typed URL). */
function sanitizeReferrer(raw: string): string | null {
  if (!raw) return null
  const url = parseUrl(raw)
  return url ? originPathname(url) : null
}

/**
 * Records a visitor landing on a 404. `path` is the URL that had no matching
 * route; `referrer` is `document.referrer` from the page that linked here, so
 * a broken *internal* link (referrer on the same origin) can be told apart
 * from a stale external/bookmarked one.
 */
export function captureDeadLinkHit(path: string, referrer: string): void {
  capture('dead_link_hit', {
    path: sanitizePath(path),
    referrer: sanitizeReferrer(referrer),
  })
}
